import React, { useState } from "react";
import { Phone, Mail, MapPin, CheckCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";

export default function ContactSection() {
    const [formData, setFormData] = useState({
        fullName: "",
        emailAddress: "",
        phoneNumber: "",
        companyName: "",
        message: "",
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submitError, setSubmitError] = useState("");

    // Callback form state
    const [callbackPhone, setCallbackPhone] = useState("");
    const [callbackError, setCallbackError] = useState("");
    const [callbackSubmitting, setCallbackSubmitting] = useState(false);
    const [callbackSubmitted, setCallbackSubmitted] = useState(false);
    const [callbackSubmitError, setCallbackSubmitError] = useState("");

    const handleCallbackSubmit = async () => {
        setCallbackSubmitError("");
        setCallbackError("");
        if (!callbackPhone.trim()) {
            setCallbackError("Phone number is required");
            return;
        }
        setCallbackSubmitting(true);
        try {
            const res = await fetch(`${BACKEND_URL}/api/contact/callback`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ phoneNumber: callbackPhone }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Submission failed");
            setCallbackSubmitted(true);
            setCallbackPhone("");
        } catch (err) {
            setCallbackSubmitError(err.message || "Something went wrong. Please try again.");
        } finally {
            setCallbackSubmitting(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: "" }));
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.fullName.trim()) newErrors.fullName = "Full Name is required";
        if (!formData.emailAddress.trim()) {
            newErrors.emailAddress = "Email Address is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailAddress.trim())) {
            newErrors.emailAddress = "Please enter a valid email address";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError("");
        if (!validate()) return;

        setSubmitting(true);
        try {
            const res = await fetch(`${BACKEND_URL}/api/contact/submit`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Submission failed");
            setSubmitted(true);
            setFormData({ fullName: "", emailAddress: "", phoneNumber: "", companyName: "", message: "" });
        } catch (err) {
            setSubmitError(err.message || "Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section className="w-full bg-white py-16 md:py-24 px-4 sm:px-6 lg:px-8 flex justify-center">
            <div className="max-w-7xl w-full">
                {/* Heading */}
                <div className="text-center mb-12">
                    <p className="uppercase text-sm tracking-wide text-gray-500 mb-2">Contact</p>
                    <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-4">
                        Ready to Transform Your Practice?
                    </h2>
                    <p className="text-gray-600 text-base max-w-2xl mx-auto">
                        Get in touch with our team to learn how we can streamline your practice management and improve patient care.
                    </p>
                </div>

                {/* Content */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Left - Send Message */}
                    <div className="bg-white shadow-md rounded-2xl p-8 border border-gray-200">
                        <h3 className="text-lg font-semibold mb-6 text-gray-900">
                            Send us a message
                        </h3>
                        <form className="space-y-4" onSubmit={handleSubmit}>
                            {/* Success message */}
                            {submitted && (
                                <div className="flex items-center gap-2 bg-green-50 text-green-700 border border-green-200 rounded-lg px-4 py-3 text-sm">
                                    <CheckCircle size={18} />
                                    Your message has been sent successfully! We'll get back to you soon.
                                </div>
                            )}
                            {submitError && (
                                <div className="bg-red-50 text-red-700 border border-red-200 rounded-lg px-4 py-3 text-sm">
                                    {submitError}
                                </div>
                            )}
                            {/* 2-column grid for inputs */}
                            <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Full Name *
                                    </label>
                                    <input
                                        type="text"
                                        name="fullName"
                                        value={formData.fullName}
                                        onChange={handleChange}
                                        className={`w-full border ${errors.fullName ? 'border-red-400' : 'border-gray-300'} rounded-lg px-4 py-2 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-black`}
                                    />
                                    {errors.fullName && <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Email Address *
                                    </label>
                                    <input
                                        type="email"
                                        name="emailAddress"
                                        value={formData.emailAddress}
                                        onChange={handleChange}
                                        className={`w-full border ${errors.emailAddress ? 'border-red-400' : 'border-gray-300'} rounded-lg px-4 py-2 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-black`}
                                    />
                                    {errors.emailAddress && <p className="text-red-500 text-xs mt-1">{errors.emailAddress}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Phone Number
                                    </label>
                                    <input
                                        type="text"
                                        name="phoneNumber"
                                        value={formData.phoneNumber}
                                        onChange={handleChange}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-black"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Company Name
                                    </label>
                                    <input
                                        type="text"
                                        name="companyName"
                                        value={formData.companyName}
                                        onChange={handleChange}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-black"
                                    />
                                </div>
                            </div>

                            {/* Message box */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Message
                                </label>
                                <textarea
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    placeholder="Tell us about your requirements..."
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-black h-72 resize-none"
                                ></textarea>
                            </div>

                            {/* Button */}
                            <button
                                type="submit"
                                disabled={submitting}
                                className="w-full bg-black text-white py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors duration-200 shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {submitting ? "Sending..." : "Send Message"}
                            </button>
                        </form>
                    </div>

                    {/* Right Side */}
                    <div className="flex flex-col gap-8">
                        {/* Request Callback */}
                        <div className="bg-white shadow-md rounded-2xl p-6 border border-gray-200">
                            <h3 className="text-lg font-semibold mb-2 text-gray-900">
                                Request a Callback
                            </h3>
                            <p className="text-gray-600 text-sm mb-4">
                                Prefer to speak directly? Our experts are standing by to discuss your needs.
                            </p>
                            {callbackSubmitted && (
                                <div className="flex items-center gap-2 bg-green-50 text-green-700 border border-green-200 rounded-lg px-4 py-3 text-sm mb-4">
                                    <CheckCircle size={18} />
                                    Callback request submitted! We'll call you soon.
                                </div>
                            )}
                            {callbackSubmitError && (
                                <div className="bg-red-50 text-red-700 border border-red-200 rounded-lg px-4 py-3 text-sm mb-4">
                                    {callbackSubmitError}
                                </div>
                            )}
                            <label className="block font-semibold text-gray-700 mb-1">
                                Your Phone Number
                            </label>
                            <input
                                type="text"
                                placeholder="+1 (514) 249-7556"
                                value={callbackPhone}
                                onChange={(e) => { setCallbackPhone(e.target.value); setCallbackError(""); }}
                                className={`w-full border ${callbackError ? 'border-red-400' : 'border-gray-300'} rounded-lg px-4 py-2 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-black mb-1`}
                            />
                            {callbackError && <p className="text-red-500 text-xs mb-3">{callbackError}</p>}
                            {!callbackError && <div className="mb-3"></div>}
                            <button
                                type="button"
                                disabled={callbackSubmitting}
                                onClick={handleCallbackSubmit}
                                className="w-full flex items-center justify-center gap-2 border border-black py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                <Phone size={18} /> {callbackSubmitting ? "Submitting..." : "Request Callback"}
                            </button>
                        </div>

                        {/* Get in Touch */}
                        <div className="bg-white shadow-md rounded-2xl p-6 border border-gray-200">
                            <h3 className="text-lg font-semibold mb-4 text-gray-900">
                                Get in Touch
                            </h3>
                            <div className="space-y-6 text-gray-700 text-sm">
                                <div>
                                    <div className="font-medium mb-1 ml-7">Phone</div>
                                    <p className="flex items-center gap-2">
                                        <Phone size={18} /> +1 (514) 249-7556
                                    </p>
                                </div>
                                <div>
                                    <div className="font-medium mb-1 ml-7">Email</div>
                                    <p className="flex items-center gap-2">
                                        <Mail size={18} /> office@geniktech.com
                                    </p>
                                </div>
                                <div>
                                    <div className="font-medium mb-1 ml-7">Location</div>
                                    <p className="flex items-start gap-2">
                                        <MapPin size={18} className="mt-0.5" />
                                        <span>
                                            1338 Wellington St. W, Unit 10
                                            <br />
                                            Ottawa, Ontario
                                            <br />
                                            K1Y 3B7, Canada
                                        </span>
                                    </p>
                                </div>
                            </div>
                            <div className="border-t border-gray-200 pt-4 mt-4 text-gray-600 text-sm">
                                <p><strong>Business Hours:</strong></p>
                                <p>Monday - Friday: 9:00 AM - 6:00 PM IST</p>
                                <p className="mb-2">Saturday: 10:00 AM - 4:00 PM IST</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
